import React, { useState, useEffect } from 'react';
import { Project, ProjectAddress, ProjectAdditionalInfo } from '../types/project';
import { Builder } from '../types/builder';
import FormSlider from './FormSlider';
import ImageUpload from './ImageUpload';
import { uploadImage } from '../services/uploadService';
import { getImageUrl } from '../services/imageService';
import api from '../services/api';
import '../styles/DrawerForm.css';
import '../styles/Buttons.css';

interface ProjectFormSliderProps {
  workspaceId: string;
  projectId?: string;
  onSuccess: () => void;
  onCancel: () => void;
}

interface ProjectFormData {
  name: string;
  projectCode: string;
  description: string;
  builderId: string;
  phase: string;
  status: string;
  contactName: string;
  contactEmail: string;
  contactWebsite: string;
  // Logo
  logoUrl: string;
  logoFile: File | null;
  logoId: string;
  deleteLogo: boolean;
  // Project Address
  projectStreet1: string;
  projectStreet2: string;
  projectCity: string;
  projectState: string;
  projectZip: string;
  projectCountry: string;
  // Office Address
  officeStreet1: string;
  officeStreet2: string;
  officeCity: string;
  officeState: string;
  officeZip: string;
  officeCountry: string;
  // Additional Info
  projectType: 'condominium' | 'townhouse' | 'semi-detached' | 'detached' | '';
  totalUnits: string;
  totalFloors: string;
  totalBuildings: string;
  totalHomes: string;
  // Dates
  constructionStartDate: string;
  estimatedCompletionDate: string;
}

const ProjectFormSlider: React.FC<ProjectFormSliderProps> = ({
  workspaceId,
  projectId,
  onSuccess,
  onCancel
}) => {
  const [builders, setBuilders] = useState<Builder[]>([]);
  const [loadingBuilders, setLoadingBuilders] = useState(true);
  const [initialFormData, setInitialFormData] = useState<ProjectFormData>({
    name: '',
    projectCode: '',
    description: '',
    builderId: '',
    phase: '',
    status: 'Active',
    contactName: '',
    contactEmail: '',
    contactWebsite: '',
    logoUrl: '',
    logoFile: null,
    logoId: '',
    deleteLogo: false,
    projectStreet1: '',
    projectStreet2: '',
    projectCity: '',
    projectState: '',
    projectZip: '',
    projectCountry: 'Canada',
    officeStreet1: '',
    officeStreet2: '',
    officeCity: '',
    officeState: '',
    officeZip: '',
    officeCountry: 'Canada',
    projectType: '',
    totalUnits: '',
    totalFloors: '',
    totalBuildings: '',
    totalHomes: '',
    constructionStartDate: '',
    estimatedCompletionDate: ''
  });

  useEffect(() => {
    fetchBuilders();
  }, [workspaceId]);

  const fetchBuilders = async () => {
    try {
      setLoadingBuilders(true);
      const response = await api.get(`/workspaces/${workspaceId}/builders`, {
        params: {
          limit: 1000,
          sortBy: 'name',
          sortOrder: 'asc'
        }
      });
      const fetchedBuilders = response.data.builders;
      setBuilders(fetchedBuilders);

      // Auto-select first builder if creating new project and builders exist
      if (!projectId && fetchedBuilders.length > 0) {
        setInitialFormData(prev => ({
          ...prev,
          builderId: fetchedBuilders[0]._id
        }));
      }
    } catch (err) {
      console.error('Error fetching builders:', err);
    } finally {
      setLoadingBuilders(false);
    }
  };

  const validateForm = (data: ProjectFormData): string | null => {
    if (!data.name.trim()) {
      return 'Project name is required';
    }
    if (!data.builderId) {
      return 'Builder is required';
    }
    return null;
  };

  const transformFetchData = (response: any): ProjectFormData => {
    const project: Project = response.project;

    return {
      name: project.name || '',
      projectCode: project.projectCode || '',
      description: project.description || '',
      builderId: project.builderId || '',
      phase: project.phase || '',
      status: project.status || 'Active',
      contactName: project.contactName || '',
      contactEmail: project.contactEmail || '',
      contactWebsite: project.contactWebsite || '',
      logoUrl: '', // Logo URL will be fetched separately when needed
      logoFile: null,
      logoId: project.logoId || '',
      deleteLogo: false,
      projectStreet1: project.projectAddress?.street1 || '',
      projectStreet2: project.projectAddress?.street2 || '',
      projectCity: project.projectAddress?.city || '',
      projectState: project.projectAddress?.state || '',
      projectZip: project.projectAddress?.zip || '',
      projectCountry: project.projectAddress?.country || 'Canada',
      officeStreet1: project.officeAddress?.street1 || '',
      officeStreet2: project.officeAddress?.street2 || '',
      officeCity: project.officeAddress?.city || '',
      officeState: project.officeAddress?.state || '',
      officeZip: project.officeAddress?.zip || '',
      officeCountry: project.officeAddress?.country || 'Canada',
      projectType: project.additionalInfo?.[0]?.type || '',
      totalUnits: project.additionalInfo?.[0]?.totalUnits?.toString() || '',
      totalFloors: project.additionalInfo?.[0]?.totalFloors?.toString() || '',
      totalBuildings: project.additionalInfo?.[0]?.totalBuildings?.toString() || '',
      totalHomes: project.additionalInfo?.[0]?.totalHomes?.toString() || '',
      constructionStartDate: project.constructionStartDate ? project.constructionStartDate.split('T')[0] : '',
      estimatedCompletionDate: project.estimatedCompletionDate ? project.estimatedCompletionDate.split('T')[0] : ''
    };
  };

  const transformSubmitData = (data: ProjectFormData) => {
    const hasProjectAddress = data.projectStreet1.trim() || data.projectCity.trim() ||
                              data.projectState.trim() || data.projectZip.trim();

    const projectAddress: ProjectAddress | undefined = hasProjectAddress ? {
      street1: data.projectStreet1.trim(),
      street2: data.projectStreet2.trim(),
      city: data.projectCity.trim(),
      state: data.projectState.trim(),
      zip: data.projectZip.trim(),
      country: data.projectCountry.trim()
    } : undefined;

    const hasOfficeAddress = data.officeStreet1.trim() || data.officeCity.trim() ||
                             data.officeState.trim() || data.officeZip.trim();

    const officeAddress: ProjectAddress | undefined = hasOfficeAddress ? {
      street1: data.officeStreet1.trim(),
      street2: data.officeStreet2.trim(),
      city: data.officeCity.trim(),
      state: data.officeState.trim(),
      zip: data.officeZip.trim(),
      country: data.officeCountry.trim()
    } : undefined;

    const additionalInfo: ProjectAdditionalInfo[] = data.projectType ? [{
      type: data.projectType as 'condominium' | 'townhouse' | 'semi-detached' | 'detached',
      totalUnits: data.totalUnits ? parseInt(data.totalUnits) : undefined,
      totalFloors: data.totalFloors ? parseInt(data.totalFloors) : undefined,
      totalBuildings: data.totalBuildings ? parseInt(data.totalBuildings) : undefined,
      totalHomes: data.totalHomes ? parseInt(data.totalHomes) : undefined
    }] : [];

    return {
      name: data.name.trim(),
      projectCode: data.projectCode.trim() || undefined,
      description: data.description.trim() || undefined,
      builderId: data.builderId,
      phase: data.phase.trim() || undefined,
      status: data.status.trim() || undefined,
      contactName: data.contactName.trim() || undefined,
      contactEmail: data.contactEmail.trim() || undefined,
      contactWebsite: data.contactWebsite.trim() || undefined,
      projectAddress,
      officeAddress,
      additionalInfo,
      constructionStartDate: data.constructionStartDate || undefined,
      estimatedCompletionDate: data.estimatedCompletionDate || undefined
    };
  };

  // Don't render the form until builders are loaded for new projects
  if (!projectId && loadingBuilders) {
    return (
      <div className="builders-loading" style={{ padding: '64px 0' }}>
        <div className="builders-loading-spinner" />
      </div>
    );
  }

  return (
    <FormSlider
      key={initialFormData.builderId} // Force remount when builder is selected
      workspaceId={workspaceId}
      itemId={projectId}
      onSuccess={onSuccess}
      onCancel={onCancel}
      apiEndpoint={`/workspaces/${workspaceId}/projects`}
      initialFormData={initialFormData}
      validateForm={validateForm}
      transformFetchData={transformFetchData}
      transformSubmitData={transformSubmitData}
      formId="project-form"
    >
      {({ formData, handleInputChange, handleFieldChange, loading }) => (
        <>
          {/* Basic Information */}
          <h3>Basic Information</h3>

          <div className="form-group">
            <label htmlFor="name" className="form-label">
              Project Name
            </label>
            <input
              type="text"
              id="name"
              name="name"
              className="form-input"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Lakeside Residences"
              required
              disabled={loading}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="projectCode" className="form-label">
                Project Code
              </label>
              <input
                type="text"
                id="projectCode"
                name="projectCode"
                className="form-input"
                value={formData.projectCode}
                onChange={handleInputChange}
                placeholder="LKS-2025-001"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="builderId" className="form-label">
                Builder
              </label>
              <select
                id="builderId"
                name="builderId"
                className="form-input"
                value={formData.builderId}
                onChange={handleInputChange}
                required
                disabled={loading || loadingBuilders}
              >
                {builders.map((builder) => (
                  <option key={builder._id} value={builder._id}>
                    {builder.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="description" className="form-label">
              Description
            </label>
            <textarea
              id="description"
              name="description"
              className="form-input"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="A luxury waterfront development featuring modern condominiums with stunning lake views and premium amenities"
              rows={3}
              disabled={loading}
              style={{ resize: 'vertical' }}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="phase" className="form-label">
                Phase
              </label>
              <input
                type="text"
                id="phase"
                name="phase"
                className="form-input"
                value={formData.phase}
                onChange={handleInputChange}
                placeholder="Phase 2"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="status" className="form-label">
                Status
              </label>
              <select
                id="status"
                name="status"
                className="form-input"
                value={formData.status}
                onChange={handleInputChange}
                disabled={loading}
              >
                <option value="Active">Active</option>
                <option value="Under Construction">Under Construction</option>
                <option value="Completed">Completed</option>
                <option value="Planning">Planning</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
          </div>

          {/* Project Details & Timeline in Two Columns */}
          <div className="form-row" style={{ alignItems: 'flex-start', gap: '24px' }}>
            {/* Project Details Column */}
            <div style={{ flex: 1 }}>
              <h3>Project Details</h3>

              <div className="form-group">
                <label htmlFor="projectType" className="form-label">
                  Project Type
                </label>
                <select
                  id="projectType"
                  name="projectType"
                  className="form-input"
                  value={formData.projectType}
                  onChange={handleInputChange}
                  disabled={loading}
                >
                  <option value="">Select Type</option>
                  <option value="condominium">Condominium</option>
                  <option value="townhouse">Townhouse</option>
                  <option value="semi-detached">Semi-Detached</option>
                  <option value="detached">Detached</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="totalUnits" className="form-label">
                  Total Units
                </label>
                <input
                  type="number"
                  id="totalUnits"
                  name="totalUnits"
                  className="form-input"
                  value={formData.totalUnits}
                  onChange={handleInputChange}
                  placeholder="325"
                  min="0"
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="totalFloors" className="form-label">
                  Total Floors
                </label>
                <input
                  type="number"
                  id="totalFloors"
                  name="totalFloors"
                  className="form-input"
                  value={formData.totalFloors}
                  onChange={handleInputChange}
                  placeholder="28"
                  min="0"
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="totalBuildings" className="form-label">
                  Total Buildings
                </label>
                <input
                  type="number"
                  id="totalBuildings"
                  name="totalBuildings"
                  className="form-input"
                  value={formData.totalBuildings}
                  onChange={handleInputChange}
                  placeholder="2"
                  min="0"
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="totalHomes" className="form-label">
                  Total Homes
                </label>
                <input
                  type="number"
                  id="totalHomes"
                  name="totalHomes"
                  className="form-input"
                  value={formData.totalHomes}
                  onChange={handleInputChange}
                  placeholder="45"
                  min="0"
                  disabled={loading}
                />
              </div>
            </div>

            {/* Project Timeline Column */}
            <div style={{ flex: 1 }}>
              <h3>Project Timeline</h3>

              <div className="form-group">
                <label htmlFor="constructionStartDate" className="form-label">
                  Construction Start Date
                </label>
                <input
                  type="date"
                  id="constructionStartDate"
                  name="constructionStartDate"
                  className="form-input"
                  value={formData.constructionStartDate}
                  onChange={handleInputChange}
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="estimatedCompletionDate" className="form-label">
                  Estimated Completion Date
                </label>
                <input
                  type="date"
                  id="estimatedCompletionDate"
                  name="estimatedCompletionDate"
                  className="form-input"
                  value={formData.estimatedCompletionDate}
                  onChange={handleInputChange}
                  disabled={loading}
                />
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <h3>Contact Information</h3>

          <div className="form-group">
            <label htmlFor="contactName" className="form-label">
              Contact Name
            </label>
            <input
              type="text"
              id="contactName"
              name="contactName"
              className="form-input"
              value={formData.contactName}
              onChange={handleInputChange}
              placeholder="Sarah Mitchell"
              disabled={loading}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="contactEmail" className="form-label">
                Contact Email
              </label>
              <input
                type="email"
                id="contactEmail"
                name="contactEmail"
                className="form-input"
                value={formData.contactEmail}
                onChange={handleInputChange}
                placeholder="sarah.mitchell@lakesideresidences.com"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="contactWebsite" className="form-label">
                Website
              </label>
              <input
                type="url"
                id="contactWebsite"
                name="contactWebsite"
                className="form-input"
                value={formData.contactWebsite}
                onChange={handleInputChange}
                placeholder="https://www.lakesideresidences.com"
                disabled={loading}
              />
            </div>
          </div>

          {/* Project Address */}
          <h3>Project Address</h3>

          <div className="form-group">
            <label htmlFor="projectStreet1" className="form-label">
              Street Address 1
            </label>
            <input
              type="text"
              id="projectStreet1"
              name="projectStreet1"
              className="form-input"
              value={formData.projectStreet1}
              onChange={handleInputChange}
              placeholder="2500 Lakeshore Boulevard West"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="projectStreet2" className="form-label">
              Street Address 2
            </label>
            <input
              type="text"
              id="projectStreet2"
              name="projectStreet2"
              className="form-input"
              value={formData.projectStreet2}
              onChange={handleInputChange}
              placeholder="Tower A"
              disabled={loading}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="projectCity" className="form-label">
                City
              </label>
              <input
                type="text"
                id="projectCity"
                name="projectCity"
                className="form-input"
                value={formData.projectCity}
                onChange={handleInputChange}
                placeholder="Toronto"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="projectState" className="form-label">
                State/Province
              </label>
              <input
                type="text"
                id="projectState"
                name="projectState"
                className="form-input"
                value={formData.projectState}
                onChange={handleInputChange}
                placeholder="Ontario"
                disabled={loading}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="projectZip" className="form-label">
                Postal/Zip Code
              </label>
              <input
                type="text"
                id="projectZip"
                name="projectZip"
                className="form-input"
                value={formData.projectZip}
                onChange={handleInputChange}
                placeholder="M6K 3P6"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="projectCountry" className="form-label">
                Country
              </label>
              <input
                type="text"
                id="projectCountry"
                name="projectCountry"
                className="form-input"
                value={formData.projectCountry}
                onChange={handleInputChange}
                placeholder="Canada"
                disabled={loading}
              />
            </div>
          </div>

          {/* Office Address */}
          <h3>Office Address</h3>

          <div className="form-group">
            <label htmlFor="officeStreet1" className="form-label">
              Street Address 1
            </label>
            <input
              type="text"
              id="officeStreet1"
              name="officeStreet1"
              className="form-input"
              value={formData.officeStreet1}
              onChange={handleInputChange}
              placeholder="150 King Street West"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="officeStreet2" className="form-label">
              Street Address 2
            </label>
            <input
              type="text"
              id="officeStreet2"
              name="officeStreet2"
              className="form-input"
              value={formData.officeStreet2}
              onChange={handleInputChange}
              placeholder="Suite 1200"
              disabled={loading}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="officeCity" className="form-label">
                City
              </label>
              <input
                type="text"
                id="officeCity"
                name="officeCity"
                className="form-input"
                value={formData.officeCity}
                onChange={handleInputChange}
                placeholder="Toronto"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="officeState" className="form-label">
                State/Province
              </label>
              <input
                type="text"
                id="officeState"
                name="officeState"
                className="form-input"
                value={formData.officeState}
                onChange={handleInputChange}
                placeholder="Ontario"
                disabled={loading}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="officeZip" className="form-label">
                Postal/Zip Code
              </label>
              <input
                type="text"
                id="officeZip"
                name="officeZip"
                className="form-input"
                value={formData.officeZip}
                onChange={handleInputChange}
                placeholder="M5H 1J9"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="officeCountry" className="form-label">
                Country
              </label>
              <input
                type="text"
                id="officeCountry"
                name="officeCountry"
                className="form-input"
                value={formData.officeCountry}
                onChange={handleInputChange}
                placeholder="Canada"
                disabled={loading}
              />
            </div>
          </div>
        </>
      )}
    </FormSlider>
  );
};

export default ProjectFormSlider;
